import { useEffect, useState } from "react";
import { BellRing, Loader2, MousePointerClick, ShoppingBag, Target, UserCheck, Users } from "lucide-react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toLocaleDateString("fr-CA", { timeZone: "America/Montreal" });
};

// Carte « Prospection » de l'aperçu admin : par sport, combien d'organismes captés,
// contactés et devenus clients — plus le nombre « à relancer » (contactés depuis
// 14 jours ou plus, sans réponse). Un clic sur un sport ouvre le DÉTAIL : qui a été
// capté, qui a été contacté (courriel utilisé, date, par qui) et qui est devenu client
// (compte boutique ou commande réelle détectés automatiquement).
export default function ProspectStats() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);      // {sport, prospects, captes, contactes, clients}
  const [detailSport, setDetailSport] = useState("");
  const [detailTab, setDetailTab] = useState("captes");
  const [detailBusy, setDetailBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/prospects/stats")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  const openDetail = async (sport, tab = "captes") => {
    setDetailSport(sport);
    setDetailTab(tab);
    setDetail(null);
    setDetailBusy(true);
    try {
      const { data: d } = await api.get("/admin/prospects/sport-details", { params: { sport } });
      setDetail(d);
    } catch { setDetail({ sport, prospects: [], captes: 0, contactes: 0, clients: 0 }); }
    finally { setDetailBusy(false); }
  };

  const tabList = !detail ? [] : {
    captes: detail.prospects,
    contactes: detail.prospects.filter((p) => p.status === "contacte" || p.status === "client"),
    clients: detail.prospects.filter((p) => p.devenu_client),
  }[detailTab] || [];

  if (!data) return null;

  return (
    <div className="mt-8 bg-surface border border-gold/15 rounded-xl p-7" data-testid="prospect-stats">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Target className="w-4 h-4 text-gold" />
        <h3 className="text-xs uppercase tracking-widest text-ivory/70">Prospection par sport</h3>
        {data.a_relancer > 0 && (
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/40 rounded-full px-3 py-1"
            data-testid="prospect-stats-relance">
            <BellRing className="w-3.5 h-3.5" /> {data.a_relancer} à relancer
          </span>
        )}
        <span className="ml-auto text-xs text-ivory/55" data-testid="prospect-stats-total">
          {data.captes} capté{data.captes > 1 ? "s" : ""} · {data.contactes} contacté{data.contactes > 1 ? "s" : ""} · {data.clients} client{data.clients > 1 ? "s" : ""}
        </span>
      </div>

      {(data.rappels || []).length > 0 && (
        <div className="mb-5 border border-amber-500/30 bg-amber-500/5 rounded-lg p-4" data-testid="prospect-stats-rappels">
          <p className="text-[11px] uppercase tracking-widest text-amber-400 mb-2">
            Relances prévues — à faire ({data.rappels.length})
          </p>
          <ul className="space-y-1.5">
            {data.rappels.map((r) => (
              <li key={r.email} className="text-xs flex flex-wrap items-center gap-x-2" data-testid={`rappel-${r.email}`}>
                <span className="text-amber-400/90 font-mono">{r.relance_le}</span>
                <span className="text-ivory/85 font-medium">{r.email}</span>
                <span className="text-ivory/50">{r.org} · {r.sport}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.sports.length === 0 ? (
        <p className="text-sm text-ivory/60" data-testid="prospect-stats-empty">
          Aucun prospect capté pour l'instant — utilisez l'onglet « Prospects » pour analyser le site d'un organisme.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-ivory/50 border-b border-ivory/10">
                <th className="pb-2 font-medium">Sport</th>
                <th className="pb-2 font-medium text-right">Captés</th>
                <th className="pb-2 font-medium text-right">Contactés</th>
                <th className="pb-2 font-medium text-right">Clients</th>
              </tr>
            </thead>
            <tbody>
              {data.sports.map((s) => (
                <tr key={s.sport} className="border-b border-ivory/5 last:border-0" data-testid={`prospect-stats-${s.sport.toLowerCase()}`}>
                  <td className="py-2 text-ivory/85">{s.sport}</td>
                  <td className="py-2 text-right">
                    <button data-testid={`detail-captes-${s.sport.toLowerCase()}`} onClick={() => openDetail(s.sport, "captes")}
                      title="Voir qui a été capté"
                      className="text-ivory/70 hover:text-gold underline decoration-dotted underline-offset-4 transition-colors">{s.captes}</button>
                  </td>
                  <td className="py-2 text-right">
                    <button data-testid={`detail-contactes-${s.sport.toLowerCase()}`} onClick={() => openDetail(s.sport, "contactes")}
                      title="Voir qui a été contacté et avec quel courriel"
                      className="text-ivory/70 hover:text-gold underline decoration-dotted underline-offset-4 transition-colors">{s.contactes}</button>
                  </td>
                  <td className="py-2 text-right">
                    <button data-testid={`detail-clients-${s.sport.toLowerCase()}`} onClick={() => openDetail(s.sport, "clients")}
                      title="Voir ceux qui sont devenus clients"
                      className={`font-semibold underline decoration-dotted underline-offset-4 transition-colors ${s.clients > 0 ? "text-emerald-400 hover:text-emerald-300" : "text-ivory/40 hover:text-gold"}`}>{s.clients}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-ivory/40 mt-2 inline-flex items-center gap-1.5">
            <MousePointerClick className="w-3 h-3" /> Cliquez un chiffre pour voir le détail (qui, courriel utilisé, date).
          </p>
        </div>
      )}
      {(data.cities || []).length > 0 && (
        <div className="mt-6" data-testid="prospect-stats-cities">
          <p className="text-[11px] uppercase tracking-widest text-gold mb-3">Par ville (recherche par ville)</p>
          {(() => {
            const pts = data.cities.filter((c) => c.lat && c.lng);
            if (!pts.length) return null;
            const center = [pts.reduce((s, c) => s + c.lat, 0) / pts.length,
                            pts.reduce((s, c) => s + c.lng, 0) / pts.length];
            return (
              <div className="mb-4 rounded-lg overflow-hidden border border-ivory/10" data-testid="prospect-cities-map">
                <style>{`.leaflet-tile-pane { filter: invert(0.92) hue-rotate(185deg) brightness(0.9) contrast(0.92) saturate(0.35); }`}</style>
                <MapContainer key={center.join(",")} center={center} zoom={7} scrollWheelZoom={false}
                  style={{ height: 260, background: "#0d0d0f" }}>
                  <TileLayer
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                  {pts.map((c) => (
                    <CircleMarker key={c.city} center={[c.lat, c.lng]}
                      radius={7 + Math.min(c.captes, 10) * 1.4}
                      pathOptions={{ color: "#d4af37", fillColor: "#d4af37", fillOpacity: 0.5, weight: 1.5 }}>
                      <Popup>
                        <strong>{c.city}</strong><br />
                        {c.captes} capté(s) · {c.contactes} contacté(s) · {c.clients} client(s)
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
            );
          })()}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-ivory/50 border-b border-ivory/10">
                  <th className="pb-2 font-medium">Ville</th>
                  <th className="pb-2 font-medium text-right">Captés</th>
                  <th className="pb-2 font-medium text-right">Contactés</th>
                  <th className="pb-2 font-medium text-right">Clients</th>
                </tr>
              </thead>
              <tbody>
                {data.cities.map((c) => (
                  <tr key={c.city} className="border-b border-ivory/5 last:border-0" data-testid={`prospect-stats-city-${c.city.toLowerCase().replace(/\s+/g, "-")}`}>
                    <td className="py-2 text-ivory/85">{c.city}</td>
                    <td className="py-2 text-right text-ivory/70">{c.captes}</td>
                    <td className="py-2 text-right text-ivory/70">{c.contactes}</td>
                    <td className={`py-2 text-right font-semibold ${c.clients > 0 ? "text-emerald-400" : "text-ivory/40"}`}>{c.clients}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="text-[11px] text-ivory/55 mt-4">
        « À relancer » = contacté depuis 14 jours ou plus, toujours sans réponse · voyez l'onglet Prospects pour la liste.
      </p>

      <Dialog open={!!detailSport} onOpenChange={(o) => { if (!o) { setDetailSport(""); setDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-ink border-gold/25 text-ivory" data-testid="prospect-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-gold">{detailSport} — détail de la prospection</DialogTitle>
            <DialogDescription className="text-ivory/55">
              Qui a été capté, qui a été contacté (courriel utilisé) et qui est devenu client.
            </DialogDescription>
          </DialogHeader>
          {detailBusy || !detail ? (
            <div className="py-10 text-center text-ivory/50"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  ["captes", `Captés (${detail.captes})`, Users],
                  ["contactes", `Contactés (${detail.contactes})`, UserCheck],
                  ["clients", `Devenus clients (${detail.clients})`, ShoppingBag],
                ].map(([key, label, Icon]) => (
                  <button key={key} data-testid={`detail-tab-${key}`} onClick={() => setDetailTab(key)}
                    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-xs transition-colors ${detailTab === key ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
              {tabList.length === 0 ? (
                <p className="text-sm text-ivory/55 py-6 text-center" data-testid="detail-empty">Personne dans cette liste pour l'instant.</p>
              ) : (
                <div className="overflow-x-auto" data-testid="detail-list">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-ivory/50 border-b border-ivory/10">
                        <th className="pb-2 font-medium">Organisme</th>
                        <th className="pb-2 font-medium">Courriel utilisé</th>
                        <th className="pb-2 font-medium">Capté le</th>
                        <th className="pb-2 font-medium">Contacté le</th>
                        <th className="pb-2 font-medium">Client</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabList.map((p) => (
                        <tr key={p.id} className="border-b border-ivory/5 last:border-0" data-testid={`detail-row-${p.id}`}>
                          <td className="py-2 pr-3 text-ivory/85">
                            {p.org || "—"}
                            {p.city && <span className="block text-[10px] text-ivory/45">{p.city}</span>}
                          </td>
                          <td className="py-2 pr-3 text-ivory/80 font-mono break-all">{p.email || "—"}</td>
                          <td className="py-2 pr-3 text-ivory/60 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                          <td className="py-2 pr-3 text-ivory/60 whitespace-nowrap">
                            {p.contacted_at ? (
                              <>
                                {fmtDate(p.contacted_at)}
                                {p.contacted_by && <span className="block text-[10px] text-ivory/45">par {p.contacted_by}</span>}
                              </>
                            ) : "—"}
                          </td>
                          <td className="py-2 whitespace-nowrap">
                            {p.devenu_client ? (
                              <span className="inline-flex flex-col gap-0.5 text-[10px] font-semibold text-emerald-400" data-testid={`detail-client-${p.id}`}>
                                {p.commandes > 0
                                  ? <span>{p.commandes} commande{p.commandes > 1 ? "s" : ""} · {p.ca_total.toFixed(2)} $</span>
                                  : <span>{p.status === "client" ? "Marqué client" : "Compte boutique"}</span>}
                              </span>
                            ) : <span className="text-ivory/35">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
